import React, { useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from "../../assets/logo.png";
import { useSupplierServices } from "../../hooks/useSupplierServices";
import {
  canNavigateTo,
  dispatchSidebarNavigate,
} from "../../utils/sidebarNavigation";

const Sidebar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { hasService } = useSupplierServices();

  const isActive = (path, includeNested = false) => {
    const current = location.pathname.toLowerCase();
    const normalized = path.toLowerCase().replace(/\/$/, "");
    if (current === normalized || current === `${normalized}/`) return true;
    if (!includeNested || normalized === "/" || normalized === "") return false;
    return current.startsWith(`${normalized}/`);
  };

  const GuardedLink = ({ to, onClick, children, ...props }) => {
    const handleClick = (event) => {
      event.preventDefault();
      setIsOpen(false);
      const target = typeof to === "string" ? to : to?.pathname || "/";
      if (!canNavigateTo(target)) {
        return;
      }
      onClick?.(event);
      dispatchSidebarNavigate();
      navigate(to);
    };

    return (
      <Link to={to} onClick={handleClick} {...props}>
        {children}
      </Link>
    );
  };

  const handleLinkClick = () => {
    setIsOpen(false);
    dispatchSidebarNavigate();
  };

  const sidebarPortal = createPortal(
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-[190] bg-black bg-opacity-50 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      <aside
        className={`
        pointer-events-auto fixed left-0 top-0 z-[200] flex h-screen w-64 flex-col border-r border-gray-200 bg-white
        transition-transform duration-300 ease-in-out
        lg:translate-x-0 ${isOpen ? "translate-x-0" : "-translate-x-full"}
      `}
      >
        {/* Logo */}
        <div className="p-3 flex items-center justify-between mr-[38%] ml-5 lg:ml-0">
          <img src={logo} alt="logo" className="w-28 h-auto mx-auto" />
          <button
            onClick={() => setIsOpen(false)}
            className="lg:hidden p-1 rounded-md hover:bg-gray-100"
          >
            {/* <span className="material-icons-outlined text-xs">close</span> */}
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-3 overflow-y-auto h-[calc(100vh-64px)] flex flex-col">
          {/* Dashboard */}
          <GuardedLink
            to="/Dashboard"
            onClick={handleLinkClick}
            className={`flex items-center px-3 py-1.5 rounded-md mb-0.5 text-sm font-medium ${isActive("/Dashboard")
              ? "text-white bg-primary-600"
              : "text-gray-700 hover:bg-gray-100"
              }`}
          >
            <span className="material-icons-outlined text-sm mr-2">
              <i className="fi fi-rr-apps"></i>
            </span>
            <span>Dashboard</span>
          </GuardedLink>

          {/* Manage Packages - Only show if package service is provided */}
          {hasService("package") && (
            <div className="mt-3">
              <p className="px-3 text-[13px] font-medium text-gray-400 mb-1">
                Manage Packages
              </p>
              <GuardedLink
                to="/packages"
                onClick={handleLinkClick}
                className={`flex items-center px-3 py-1.5 rounded-md mb-0.5 text-sm font-medium ${isActive("/packages", true)
                  ? "text-white bg-primary-600"
                  : "text-gray-700 hover:bg-gray-100"
                  }`}
              >
                <span className="material-icons-outlined text-sm mr-2">
                  <i className="fi fi-rr-backpack"></i>
                </span>
                <span>Packages</span>
              </GuardedLink>

            </div>
          )}

          {/* Manage Events - Only show if event service is provided */}
          {hasService("event") && (
            <div className="mt-3">
              <p className="px-3 text-[13px] font-medium text-gray-400 mb-1">
                Manage Events
              </p>
              <GuardedLink
                to="/events"
                onClick={handleLinkClick}
                className={`flex items-center px-3 py-1.5 rounded-md mb-0.5 text-sm font-medium ${isActive("/events", true)
                  ? "text-white bg-primary-600"
                  : "text-gray-700 hover:bg-gray-100"
                  }`}
              >
                <span className="material-icons-outlined text-sm mr-2">
                  <i className="fi fi-rr-calendar-star"></i>
                </span>
                <span>Events</span>
              </GuardedLink>
            </div>
          )}

          {/* Manage Attractions - Only show if attraction service is provided */}
          {hasService("attraction") && (
            <div className="mt-3">
              <p className="px-3 text-[13px] font-medium text-gray-400 mb-1">
                Manage Attractions
              </p>
              <GuardedLink
                to="/Attractions"
                onClick={handleLinkClick}
                className={`flex items-center px-3 py-1.5 rounded-md mb-0.5 text-sm font-medium ${isActive("/Attractions", true)
                  ? "text-white bg-primary-600"
                  : "text-gray-700 hover:bg-gray-100"
                  }`}
              >
                <span className="material-icons-outlined text-sm mr-2">
                  <i className="fi fi-rr-map-marker"></i>
                </span>
                <span>Attractions</span>
              </GuardedLink>
            </div>
          )}

          {/* Manage Activity */}
          {hasService("activity") && (
            <div className="mt-3">
              <p className="px-3 text-[13px] font-medium text-gray-400 mb-1">
                Manage Activity
              </p>
              <GuardedLink
                to="/activities"
                onClick={handleLinkClick}
                className={`flex items-center px-3 py-1.5 rounded-md mb-0.5 text-sm font-medium ${isActive("/activities")
                  ? "text-white bg-primary-600"
                  : "text-gray-700 hover:bg-gray-100"
                  }`}
              >
                <span className="material-icons-outlined text-sm mr-2">
                  <i className="fi fi-rr-hiking"></i>
                </span>
                <span>Activity</span>
              </GuardedLink>
            </div>
          )}

          {/* Manage Rental */}
          {hasService("rental") && (
            <div className="mt-3">
              <p className="px-3 text-[13px] font-medium text-gray-400 mb-1">
                Manage Rental
              </p>
              <GuardedLink
                to="/rentals"
                onClick={handleLinkClick}
                className={`flex items-center px-3 py-1.5 rounded-md mb-0.5 text-sm font-medium ${isActive("/rentals")
                  ? "text-white bg-primary-600"
                  : "text-gray-700 hover:bg-gray-100"
                  }`}
              >
                <span className="material-icons-outlined text-sm mr-2">
                  <i className="fi fi-rr-car"></i>
                </span>
                <span>Rentals</span>
              </GuardedLink>
              <GuardedLink
                to="/rentals/management"
                onClick={handleLinkClick}
                className={`flex items-center px-3 py-1.5 rounded-md mb-0.5 text-sm font-medium ${isActive("/rentals/management")
                  ? "text-white bg-primary-600"
                  : "text-gray-700 hover:bg-gray-100"
                  }`}
              >
                <span className="material-icons-outlined text-sm mr-2">
                  <i className="fi fi-rr-clipboard-list"></i>
                </span>
                <span>Rental management</span>
              </GuardedLink>
              <GuardedLink
                to="/rental-category"
                onClick={handleLinkClick}
                className={`flex items-center px-3 py-1.5 rounded-md mb-0.5 text-sm font-medium ${isActive("/rental-category")
                  ? "text-white bg-primary-600"
                  : "text-gray-700 hover:bg-gray-100"
                  }`}
              >
                <span className="material-icons-outlined text-sm mr-2">
                  <i className="fi fi-rr-tags"></i>
                </span>
                <span>Rental Category</span>
              </GuardedLink>
              <GuardedLink
                to="/rental-sub-category"
                onClick={handleLinkClick}
                className={`flex items-center px-3 py-1.5 rounded-md mb-0.5 text-sm font-medium ${isActive("/rental-sub-category")
                  ? "text-white bg-primary-600"
                  : "text-gray-700 hover:bg-gray-100"
                  }`}
              >
                <span className="material-icons-outlined text-sm mr-2">
                  <i className="fi fi-rr-apps"></i>
                </span>
                <span>Rental Sub Category</span>
              </GuardedLink>
            </div>
          )}

          {/* Bookings */}
          <div className="mt-3">
            <p className="px-3 text-[13px] font-medium text-gray-400 mb-1">
              Bookings
            </p>
            <GuardedLink
              to="/booking"
              onClick={handleLinkClick}
              className={`flex items-center px-3 py-1.5 rounded-md mb-0.5 text-sm font-medium ${isActive("/booking")
                ? "text-white bg-primary-600"
                : "text-gray-700 hover:bg-gray-100"
                }`}
            >
              <span className="material-icons-outlined text-sm mr-2">
                <i className="fi fi-rr-ticket"></i>
              </span>
              <span>Booking</span>
            </GuardedLink>
            <GuardedLink
              to="/transactions"
              onClick={handleLinkClick}
              className={`flex items-center px-3 py-1.5 rounded-md mb-0.5 text-sm font-medium ${isActive("/transactions")
                ? "text-white bg-primary-600"
                : "text-gray-700 hover:bg-gray-100"
                }`}
            >
              <span className="material-icons-outlined text-sm mr-2">
                <i className="fi fi-rr-money-bills"></i>
              </span>
              <span>Transactions</span>
            </GuardedLink>
          </div>

          {/*  Master Forms - Show if package or rental service is provided */}
          {(hasService("package") || hasService("rental")) && (
            <div className="mt-3">
              <p className="px-3 text-[13px] font-medium text-gray-400 mb-1">
                Master Forms
              </p>
              <GuardedLink
                to="/Hotels"
                onClick={handleLinkClick}
                className={`flex items-center px-3 py-1.5 rounded-md mb-0.5 text-sm font-medium ${isActive("/Hotels")
                  ? "text-white bg-primary-600"
                  : "text-gray-700 hover:bg-gray-100"
                  }`}
              >
                <span className="material-icons-outlined text-sm mr-2">
                  <i className="fi fi-rr-bed"></i>
                </span>
                <span>Hotels</span>
              </GuardedLink>
            </div>
          )}

          {/* Profile & Logout - Sticky at bottom */}
          <div className="mt-auto pt-4 border-t border-gray-200">
            <GuardedLink
              to="/profile"
              onClick={handleLinkClick}
              className={`w-full flex items-center px-3 py-2 rounded-md text-sm font-medium mb-2 ${isActive("/profile")
                ? "text-white bg-primary-600"
                : "text-gray-700 hover:bg-gray-100"
                } transition-colors duration-200`}
            >
              <span className="material-icons-outlined text-sm mr-2">
                <i className="fi fi-rr-user"></i>
              </span>
              <span>Profile</span>
            </GuardedLink>
            <button
              onClick={() => {
                localStorage.removeItem("token");
                localStorage.removeItem("user");
                window.location.href = "/login";
              }}
              className="w-full flex items-center px-3 py-2.5 rounded-xl text-sm font-bold text-red-600 hover:bg-red-50 transition-all group"
            >
              <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-red-50 group-hover:bg-red-100 mr-3 transition-colors">
                <i className="fi fi-rr-exit text-sm mt-0.5"></i>
              </span>
              <span>Sign Out</span>
            </button>
          </div>
        </nav>
      </aside>
    </>,
    document.body
  );

  return (
    <>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed left-4 top-3 z-[210] -mt-1 ml-[3px] rounded-md bg-white p-1 shadow-sm transition-colors duration-200 hover:bg-gray-50 lg:hidden"
      >
        <span className="material-icons-outlined text-[10px] text-gray-600 mt-0.5">
          {isOpen ? (
            <i className="fi fi-rr-cross mb-[2%]"></i>
          ) : (
            <i className="fi fi-rr-menu-burger mb-[2%]"></i>
          )}
        </span>
      </button>
      {sidebarPortal}
    </>
  );
};

export default Sidebar;
